<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, useTemplateRef } from 'vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import brandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import AboutAITutorModal from '@/components/Tutor/AboutAITutorModal.vue'
import TutorSessionRating from '@/components/Tutor/TutorSessionRating.vue'
import { tutorModule } from '@/store/tutor/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { userModule } from '@/store/user/module'
import { quizModule } from '@/store/quiz/module'
import { screenModule } from '@/store/screen/module'
import { analyticsModule } from '@/store/analytics/module'
import type { TQuizMode } from '@/store/quiz/state'
import * as utils from '@/utils'

const {
    Button: PocketButton,
    Icon,
    Textarea: PocketTextarea,
    Tooltip,
} = UIKit

const props = withDefaults(
    defineProps<{
        questionIndex: number
        currentQuestion?: Study.Class.QuestionJSON | null
        currentExam?: Study.Class.ExamMetadataJSON | ClientExam | null
        quizMode?: TQuizMode | null
        explanationPromptToSend?: string | null
        learningTagQuestion?: boolean
        currentQuestionAnswerSeed?: number[] | null
    }>(),
    {
        currentQuestion: null,
        currentExam: null,
        quizMode: null,
        explanationPromptToSend: null,
        learningTagQuestion: false,
        currentQuestionAnswerSeed: null,
    }
)

const emit = defineEmits<{
    promptMessageSent: []
}>()

const isLoading = ref(true)
const showAITutorModal = ref(false)
const srAnnouncement = ref('')
const showReferences = ref(false)
const requestText = ref('')
const trimmedRequestText = computed(() => requestText.value.trim())
const showMinimizeTooltip = ref(false)
const textareaRef = useTemplateRef('textareaRef')
const tutorChatMessages = useTemplateRef<HTMLElement>('tutor-chat-messages')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const timeOfDayGreeting = computed(() => {
    const timeOfDay = utils.timeOfDay()
    if (timeOfDay === 'early') {
        return 'morning'
    }
    if (timeOfDay === 'midnight') {
        return 'evening'
    }
    return timeOfDay
})

const activeQuiz = computed(() => {
    return quizModule.getters.getActiveQuiz()
})

const activeQuestionIndex = computed(() => {
    return activeQuiz.value?.currentQuestionIndex
})

const chat = computed(() => {
    return tutorModule.state.chat.length
        ? tutorModule.state.chat
        : []
})

const chatCost = computed(() => {
    return tutorModule.state.cost
})

const nudgePrimingMessageType = computed(() => {
    return tutorModule.state.nudgePrimingMessageType
})

const primingMessage = computed(() => {
    if (nudgePrimingMessageType.value === 'eya') {
        return 'Why do you think this is the answer? Explain your thinking before checking.'
    }

    if (nudgePrimingMessageType.value === 'incorrectAnswer') {
        return 'Do you understand why this is the correct answer?'
    }

    if (nudgePrimingMessageType.value === 'stumped') {
        return 'Want some help breaking this question down?'
    }

    if (chat.value.length !== 0 || props.learningTagQuestion) {
        return null
    }

    const greetingQuestionIndex = tutorModule.state.greetingQuestionIndex
    if (!tutorModule.state.hasOpenedChatDuringQuiz || activeQuestionIndex.value !== greetingQuestionIndex) {
        return null
    }

    return `Good ${timeOfDayGreeting.value}! Want to talk through this question?`
})

const isTutorResponding = computed(() => {
    return tutorModule.state.isTutorResponding
})

const isLoadingSessionHistory = computed(() => {
    return tutorModule.state.isLoadingSessionHistory
})

const questionReferences = computed(() => {
    return props.currentQuestion?.references
})

const scannedTextBookReferenceError = computed(() => {
    return tutorModule.state.textbookSearch.error
})

const isLoadingTextBookReferences = computed(() => {
    return tutorModule.state.textbookSearch.isLoading
})

const tutorReferences = computed(() => {
    return tutorModule.getters.getPageReferences()
})

const pageReferences = computed(() => {
    if (tutorReferences.value.length && !scannedTextBookReferenceError.value) {
        const sortedReferences = tutorReferences.value.map(tr => ({
            source: tr.source,
            pages: tr.pages.sort((a, b) => a.page - b.page),
        }))
        return sortedReferences
    }

    return (questionReferences.value || []).map(r => ({
        source: r,
        pages: [],
    }))
})

const showSessionRating = computed(() => {
    const last = chat.value[chat.value.length - 1]
    return !isTutorResponding.value && chat.value.length > 0 && last?.role === 'assistant'
})

const textareaMinHeight = computed(() => {
    return breakpoint.value === 'black-bear' ? '42px' : '38px'
})

const chatUuid = computed(() => {
    return tutorModule.getters.getChatUuid(props.currentQuestion?.serial, props.currentExam?.objectId)
})

const referencePageCount = computed(() => {
    return (pageReferences.value || []).reduce((acc, r) => {
        acc += r.pages.length
        return acc
    }, 0)
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    if (props.explanationPromptToSend?.trim()) {
        const explanationPromptRequest = props.explanationPromptToSend.trim()
        tutorModule.actions.sendTutorRequest({
            request: explanationPromptRequest,
            primingMessage: primingMessage.value,
            choiceSeed: props.currentQuestionAnswerSeed,
        })
        emit('promptMessageSent')
    }

    scrollTutorChat()
    if (textareaRef.value) {
        const el = textareaRef.value?.$el?.querySelector?.('.uikit-textarea__textarea') as HTMLTextAreaElement | null
        el?.focus()
    }

    if (primingMessage.value) {
        primingMessageShownEvent()
    }

    isLoading.value = false
})

const scrollTutorChat = () => {
    const el = tutorChatMessages.value
    if (el) {
        el.scrollTop = el.scrollHeight
    }
}

const toggleTutorChat = () => {
    tutorModule.actions.toggleTutorChat(props.currentQuestion?.serial, props.currentExam?.objectId)
}

const focusInput = () => {
    nextTick(() => {
        const textareaEl = textareaRef.value?.$el?.querySelector?.(
            '.uikit-textarea__textarea'
        ) as HTMLTextAreaElement | null
        textareaEl?.focus()
    })
}

// Expose focusInput so that the Quiz component can set the focus
// on the Textarea when a user clicks on the Tutor Icon in TutorExplanationPrompts
defineExpose({
    focusInput,
})

const adjustTextarea = () => {
    const el = textareaRef.value?.$el?.querySelector?.('.uikit-textarea__textarea') as HTMLTextAreaElement | null
    if (!el) {
        return
    }
    const computedMinHeight = window.getComputedStyle(el).minHeight
    const minHeight = computedMinHeight && computedMinHeight !== 'auto'
        ? computedMinHeight
        : textareaMinHeight.value
    el.style.height = minHeight
    nextTick(() => {
        if (!el.value?.trim()) {
            el.style.height = minHeight
            return
        }
        el.style.height = `${el.scrollHeight}px`
    })
}

const sendRequest = () => {
    if (!trimmedRequestText.value || trimmedRequestText.value.length > utils.tutorInputMaxLength) {
        return
    }
    const userRequest = trimmedRequestText.value

    requestText.value = ''
    tutorModule.actions.sendTutorRequest({
        request: userRequest,
        primingMessage: primingMessage.value,
        choiceSeed: props.currentQuestionAnswerSeed,
    })
    tutorModule.state.nudgePrimingMessageType = null
    adjustTextarea()
}

const toggleReferences = () => {
    showReferences.value = !showReferences.value

    if (showReferences.value) {
        analyticsModule.actions.amplitudeTrack('Tutor References Viewed', {
            chatUuid: chatUuid.value || '',
            questionSerial: props.currentQuestion?.serial,
            examGuid: activeQuiz.value?.examGuid,
            referenceCount: referencePageCount.value,
        })
    }
}

const primingMessageShownEvent = () => {
    analyticsModule.actions.amplitudeTrack('Tutor Priming Message Received', {
        chatUuid: chatUuid.value || '',
        cost: chatCost.value,
        examMetadataId: props.currentExam?.objectId || undefined,
        examName: props.currentExam?.nativeAppName || '',
        quizMode: props.quizMode ? Number(utils.getNumericQuizMode(props.quizMode)) : 10000,
        serial: props.currentQuestion?.serial,
        primingType: nudgePrimingMessageType.value === 'eya'
            ? 'Explain Your Answer'
            : nudgePrimingMessageType.value === 'incorrectAnswer'
                ? 'Incorrect Answer'
                : nudgePrimingMessageType.value === 'stumped'
                    ? 'Stumped'
                    : 'General',
    })
}

const rateSession = (rating: -1 | 0 | 1) => {
    tutorModule.actions.rateTutorSession(rating)

    analyticsModule.actions.amplitudeTrack(
        'Tutor Chat Rated',
        {
            examMetadataId: props.currentExam?.objectId || '',
            examName: props.currentExam?.nativeAppName || '',
            serial: props.currentQuestion?.serial || '',
            quizMode: props.quizMode ? Number(utils.getNumericQuizMode(props.quizMode)) : 10000,
            chatUuid: chatUuid.value || '',
            chatRating: rating === 1
                ? 'Thumbs Up'
                : rating === -1
                    ? 'Thumbs Down'
                    : 'None',
            cost: tutorModule.state.cost || 0,
        }
    )
}

// Send a tutor request when a user selects an explanation prompt
// in the TutorExplanationPrompts component
watch(
    () => props.explanationPromptToSend,
    (message) => {
        if (!message?.trim() || isLoading.value) {
            return
        }
        const explanationPromptRequest = message.trim()
        tutorModule.actions.sendTutorRequest({
            request: explanationPromptRequest,
            primingMessage: primingMessage.value,
            choiceSeed: props.currentQuestionAnswerSeed,
        })
        emit('promptMessageSent')
    }
)

watch(chat, () => {
    nextTick(() => setTimeout(() => scrollTutorChat(), 1))
    tutorModule.state.nudgePrimingMessageType = null
}, { deep: true })

watch(activeQuestionIndex, () => {
    requestText.value = ''
    showReferences.value = false
})

watch(breakpoint, () => {
    if (requestText.value) {
        nextTick(() => {
            adjustTextarea()
        })
    } else {
        adjustTextarea()
    }
})

watch(isTutorResponding, (val: boolean, oldVal: boolean) => {
    if (val === true) {
        srAnnouncement.value = 'Tutor is generating a response'
    } else if (oldVal === true && val === false) {
        const lastMessage = chat.value[chat.value.length - 1]
        if (lastMessage?.role === 'assistant') {
            srAnnouncement.value = ''
            nextTick(() => {
                srAnnouncement.value = 'Tutor responded with: ' + lastMessage.content
                    .replace(/\*\*(.+?)\*\*/g, '$1')
                    .replace(/\*(.+?)\*/g, '$1')
                focusInput()
            })
        } else {
            nextTick(() => focusInput())
        }
    }
})

watch(primingMessage, (val: string | null, oldVal: string | null) => {
    if (val && val !== oldVal) {
        // fire amplitude event
        primingMessageShownEvent()
    }
})

// Escape HTML to prevent XSS
// then convert **words** to bold and *words* to italic
// and add paragraph breaks
const formatTutorMessageContent = (raw: string): string => {
    if (!raw) {
        return ''
    }
    const escaped = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
}
</script>

<template>
    <AboutAITutorModal v-if="showAITutorModal" @close="showAITutorModal = false" />
    <div
        ref="tutorChatRef"
        class="tutor-chat"
        role="region"
        :aria-label="`Tutor chat for question ${ questionIndex }`"
    >
        <header class="tutor-chat__header" v-dark>
            <div
                class="tutor-chat__header-left"
            >
                <Icon
                    type="tutorChat"
                    class="tutor-chat__header-icon"
                />
                <div class="tutor-chat__header-title">Tutor</div>
                <div class="tutor-chat__header-question">Question {{ questionIndex }}</div>
            </div>
            <PocketButton
                class="tutor-chat__minimize"
                aria-label="Close tutor chat"
                type="icon"
                icon="minimize"
                @focus="showMinimizeTooltip = true"
                @blur="showMinimizeTooltip = false"
                @mouseenter="showMinimizeTooltip = true"
                @mouseleave="showMinimizeTooltip = false"
                @click="toggleTutorChat"
                @mousedown.prevent
            >
                <Tooltip
                    v-if="showMinimizeTooltip && breakpoint !== 'black-bear'"
                    class="tutor-chat__minimize-tooltip"
                    :key="'minimize'"
                    :is-dark-mode="isDarkMode"
                    :theme="'leftalign'"
                    :styles="{
                        'backgroundColor': brandColors.fog,
                        'color': brandColors.brandBlack,
                        'white-space': 'nowrap',
                    }"
                >
                    Minimize Chat
                </Tooltip>
            </PocketButton>
        </header>
        <div
            ref="tutor-chat-messages"
            class="tutor-chat__messages"
            aria-describedby="tutor-chat-intro"
        >
            <div
                v-if="learningTagQuestion"
                id="tutor-chat-intro"
                class="tutor-chat__intro"

            >
                <div class="tutor-chat__intro-seen-before">
                    You’ve seen this question before so you might remember what the correct answer is.
                    Let’s go deeper to make sure you understand why.
                </div>
                <div class="tutor-chat__intro-why-correct">
                    After you select your answer, tell me why you think it is correct.
                </div>
            </div>
            <div
                v-else
                id="tutor-chat-intro"
                class="tutor-chat__intro"
            >
                Each question starts a new chat. You can go back to any question to pick back up or review the chat.
            </div>
            <div
                v-for="(message, index) in chat"
                :key="message.content"
                class="tutor-chat__message"
                :class="[`tutor-chat__message--${message.role}`]"
            >
                <div
                    class="tutor-chat__message-inner"
                    :class="[`tutor-chat__message-inner--${message.role}`]"
                    v-dark
                >
                    <div
                        v-if="message.content === 'Thinking...' && message.role === 'system'"
                        class="tutor-chat__system-loading-message-inner-text"
                        aria-hidden="true"
                    >
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </div>
                    <div
                        v-else
                        class="tutor-chat__message-inner-text"
                        :class="{ 'tutor-chat__message-inner-text--streaming': message.isStreaming }"
                        :aria-live="message.isStreaming ? 'off' : undefined"
                        v-dark
                        v-html="formatTutorMessageContent(message.content)"
                    />
                    <div
                        v-if="
                            !message.isStreaming
                                && ((index === 1 && message.role === 'assistant')
                                    ||(index === 2 && message.role === 'assistant'))
                                && (pageReferences.length || questionReferences?.length)"
                        class="tutor-chat__message-references-wrap"
                        v-dark
                    >
                        <div
                            class="tutor-chat__message-references-toggle"
                            role="button"
                            tabindex="0"
                            :aria-expanded="showReferences"
                            @click="toggleReferences"
                            @keydown.enter.prevent="toggleReferences"
                            @keydown.space.prevent="toggleReferences"
                        >
                            {{ showReferences ? 'Hide' : 'Show' }} References
                            <Icon
                                class="tutor-chat__message-references-icon"
                                :class="{ 'tutor-chat__message-references-icon--showing-refs': showReferences }"
                                type="accordionArrow"
                                aria-hidden="true"
                            />
                        </div>
                        <div
                            v-if="showReferences"
                            class="tutor-chat__message-references-content"
                            v-dark
                        >
                            <template v-if="isLoadingTextBookReferences">
                                <div class="tutor-chat__loading-scanned-textbook-references">
                                    Loading textbook references... This can take up to 5-10 seconds.
                                </div>
                            </template>
                            <template v-else>
                                <div
                                    class="tutor-chat__reference"
                                    :key="reference.source"
                                    v-for="reference in pageReferences"
                                >
                                    <template v-if="!tutorReferences.length">
                                        <div class="tutor-chat__reference-title" v-html="reference.source" />
                                    </template>
                                    <template v-else>
                                        <div class="tutor-chat__reference-title">
                                            <em>{{ reference.source }}</em>
                                        </div>
                                    </template>
                                    <ul class="tutor-chat__reference-pages" v-if="reference.pages.length">
                                        <li
                                            v-for="page in reference.pages"
                                            :key="page.page"
                                            class="tutor-chat__reference-page"
                                        >
                                            Pg {{ page.page }}: {{ page.explanation }}
                                        </li>
                                    </ul>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <div class="tutor-chat__actions" v-if="showSessionRating">
                <TutorSessionRating
                    class="tutor-chat__rating"
                    @ratingSelected="rateSession"
                />
            </div>
            <div
                v-if="primingMessage"
                class="tutor-chat__primer-message"
                aria-live="polite"
            >
                {{ primingMessage }}
            </div>
        </div>
        <div
            class="tutor-chat__sr-live-region"
            aria-live="polite"
            aria-atomic="true"
        >{{ srAnnouncement }}</div>
        <div class="tutor-chat__footer">
            <div class="tutor-chat__input-row" v-dark>
                <div
                    class="tutor-chat__input-wrap"
                    @keydown.stop
                    @keyup.stop
                >
                    <PocketTextarea
                        ref="textareaRef"
                        v-model="requestText"
                        class="tutor-chat__input"
                        :placeholder="chat.length || nudgePrimingMessageType ? 'Reply...' : 'Ask something...'"
                        aria-label="Message Tutor"
                        :auto-focus="true"
                        :is-dark-mode="true"
                        :height="textareaMinHeight"
                        width="100%"
                        :disabled="isTutorResponding || isLoadingSessionHistory"
                        @keydown.enter.exact.prevent="sendRequest"
                        @input="adjustTextarea"
                    />
                    <PocketButton
                        class="tutor-chat__send"
                        aria-label="Send message"
                        :disabled="!trimmedRequestText || trimmedRequestText.length > utils.tutorInputMaxLength
                            || isTutorResponding || isLoadingSessionHistory"
                        @click="sendRequest"
                    >
                        <Icon type="arrow" class="tutor-chat__send-icon" />
                    </PocketButton>
                </div>
            </div>
            <div
                class="tutor-chat__input-length-warning"
                :class="{
                    'tutor-chat__input-length-warning--visible':
                        trimmedRequestText.length > utils.tutorInputMaxLength,
                }"
                role="status"
                aria-live="polite"
            >
                <template v-if="trimmedRequestText.length > utils.tutorInputMaxLength">
                    Message too long: {{ trimmedRequestText.length }} / {{ utils.tutorInputMaxLength }} characters
                </template>
            </div>
            <div class="tutor-chat__tutor-ai-disclaimer">
                <PocketButton
                    class="tutor-chat__tutor-ai-disclaimer-btn"
                    aria-label="Open AI disclaimer"
                    type="tertiary"
                    :is-dark-mode="true"
                    @click="showAITutorModal = true"
                    @mousedown.prevent
                >
                    Tutor
                </PocketButton> is powered by AI.
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.tutor-chat {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    container-type: size;

    @keyframes ai-feedback-loading {
        0% {
            opacity: 1;
        }
        50% {
            opacity: 0.2;
        }
        100% {
            opacity: 0.2;
        }
    }

    @keyframes blink-cursor {
        50% {
            opacity: 0;
        }
    }

    &__header {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        height: 44px;
        color: #fff;
    }

    &__header-left {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__header-icon {
        flex-shrink: 0;
        width: 27px;
    }

    &__header-title,
    &__header-question {
        color: $white;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
    }

    &__header-question {
        color: rgba($white, 0.70);
        margin-top: 1px;
        margin-left: -2px;
    }

    &__minimize {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        padding: 0;

        :deep(svg) {
            color: $banana-bread
        }

        &:hover {
            background: $brand-black;
        }
    }

    &__minimize-tooltip {
        position: absolute;
        top: -34px;
        left: 12px;
    }

    &__messages {
        flex: 1;
        overflow: auto;
        padding: 15px 9px 30px;
        border-top: 1px solid rgba($pewter, 0.30);
        border-bottom: 1px solid rgba($pewter, 0.30);
    }

    &__intro {
        margin: 0 0 12px;
        padding: 0 6px;
        color: $cloudy;
        font-size: 13px;
        font-weight: 400;
        line-height: 18px;
    }

    &__intro-why-correct {
        margin: 8px 0 12px;
    }

    &__primer-message {
        padding: 0 6px;
        margin-bottom: 12px;
        color: $white;
        font-size: 15px;
        font-weight: 500;
        line-height: 18px;
    }

    &__message {
        margin-bottom: 12px;
        padding: 6px 16px 6px 6px;

        &--user {
            text-align: right;
            padding: 0;
        }

        &--assistant {
            text-align: left;
        }
    }

    &__message-inner {
        border-radius: 10px;
        display: inline-block;
        max-width: 80%;
        text-align: left;

        &--user {
            background-color: rgba($buttermilk, 0.20);
            padding: 8px 12px;
        }

        &--assistant {
            max-width: 342px;
        }
    }

    &__system-loading-message-inner-text {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        max-width: none;
        font-weight: 500;
        letter-spacing: -0.1px;
        line-height: 0;

        span {
            position: relative;
            display: inline-block;
            width: 6px;
            height: 6px;
            overflow: visible;
        }

        span::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: rgba($pewter, 0.85);
            transform: scale(1);
            transform-origin: center;
        }

        span:first-child {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0s;
            animation-timing-function: linear;
        }

        span:nth-child(2) {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0.3s;
            animation-timing-function: linear;
        }

        span:nth-child(3) {
            animation-name: ai-feedback-loading;
            animation-duration: 1.2s;
            animation-iteration-count: infinite;
            animation-delay: 0.6s;
            animation-timing-function: linear;
        }

        :deep(p) {
            margin: 0 0 10px;
        }
    }

    &__message-inner-text {
        color: $white;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        &--streaming::after {
            content: '|';
            display: inline-block;
            margin-left: 2px;
            color: $brand-blue;
            font-weight: 600;
            animation: blink-cursor 1s linear infinite;
        }

        :deep(reference) {
            margin-bottom: 6px;
            display: list-item;
            box-sizing: border-box;
            font-size: 13px;
            line-height: 14px;
            margin-top: 10px;
            font-style: italic;
            margin-left: 15px;
        }
    }

    &__message-references-toggle {
        margin: 12px 0 8px 0;
        color: $banana-bread;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        cursor: pointer;

        &:hover {
            color: $butterscotch;
        }
    }

    &__message-references-icon {
        margin-left: 5px;

        &--showing-refs {
            transform: rotate(180deg);
        }
    }

    &__message-references-wrap {
        margin-top: 18px;
    }

    &__message-references-content {
        width: 100%;
        color: $pearl;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        margin-top: 20px;
        margin-bottom: -2px;
    }

    &__reference {
        margin-bottom: 14px;

        &:deep(p) {
            margin: 0;
        }
    }

    &__reference-title {
        margin-bottom: 6px;
    }

    &__reference-pages {
        margin: 0;
        padding: 0 0 0 22px;
    }

    &__reference-page {
        margin-bottom: 14px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
    }

    &__rating {
        margin-bottom: 16px;
    }

    &__sr-live-region {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        white-space: nowrap;
        border: 0;
    }

    &__footer {
        flex-shrink: 0;
        padding: 15px 15px 12px;
    }

    &__input-row {
        display: flex;
        margin-bottom: 8px;
    }

    &__input-length-warning {
        &--visible {
            color: $banana-bread;
            font-size: 12px;
            font-weight: 500;
            margin: -4px 4px 8px;
        }
    }

    &__input-wrap {
        position: relative;
        flex: 1;
    }

    &__input {
        :deep(.uikit-textarea__textarea) {
            min-height: 38px;
            max-height: 50cqh;
            resize: none;
            overflow-y: auto;
            background-color: $moonlit-ocean;
            color: $white;
            line-height: 1.25;
            padding: 8px 42px 8px 12px;
            font-size: 15px;
            border-radius: 6px;

            &::placeholder {
                color: rgba($white, 0.70);
            }

            @include breakpoint(black-bear) {
                height: 42px;
                min-height: 42px;
                padding: 11px 42px 11px 12px;
                border-radius: 5px;
            }

            &::-webkit-scrollbar {
                width: 0;
                height: 0;
            }
        }
    }

    &__send {
        position: absolute;
        right: 4px;
        bottom: 9px;
        z-index: 2;
        flex-shrink: 0;
        width: 30px;
        height: 29px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $banana-bread;
        border: none;
        border-radius: 4px;
        color: #fff;
        cursor: pointer;

        &:hover:not(:disabled) {
            background-color: $butterscotch;
        }

        &:disabled {
            background-color: rgba($banana-bread, 0.40);
        }

        :deep(svg) {
            margin-top: 4px;
            width: 16px;
            height: 13px;
        }

        @include breakpoint(black-bear) {
            width: 38px;
            height: 35px;
            bottom: 8px;
            border-radius: 3px;

            :deep(svg) {
                margin-top: 3px;
                width: 19px;
                height: 19px;
            }
        }
    }

    &__send-icon {
        transform: rotate(-90deg);
        color: $brand-black;
    }

    &__tutor-ai-disclaimer {
        text-align: center;
        color: $cloudy;
        font-size: 12px;
        font-weight: 400;
    }

    &__tutor-ai-disclaimer-btn {
        color: $banana-bread;
        font-size: 12px;
        font-weight: 500;

        &:hover {
            color: $butterscotch;
        }
    }

    :deep(.uikit-btn--icon) {
        display: flex;
        align-items: flex-start;
        padding: 0 4px;
    }
}
</style>
